// MO7.1, MO7.1M
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { BiData, SearchClickBiEvent } from '@wix/da-bi/pkg/events';
import { Section } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import VisuallyHidden from '@wix/da-a11y/pkg/components/VisuallyHidden';
import shortenNumber from '@wix/da-ds/pkg/utils/shortenNumber';
import InputField from '@wix/da-ds/pkg/formControls/lego/InputField';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import SearchIcon from '@wix/da-ds/pkg/Icons/24x24/Search';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import TagList from '@wix/da-ds/pkg/TagList';
import {
  ThemeOptions,
  ThemeOverride,
} from '@wix/da-react-context/pkg/ThemeContext';

import s from './ShopPageHeader.scss';

export interface Props {
  searchPageType?: string;
  searchTerm?: string;
  totalResults?: number;
}

const SEARCH_TAGS = [
  // note: keep the names plain with no spaces as they're used in the click_info of the BI
  { name: 'Halloween', searchTerm: 'Halloween' },
  { name: 'Adoptables', searchTerm: 'Adoptables' },
  { name: 'Avatar', searchTerm: 'Avatar' },
  { name: 'Portrait', searchTerm: 'Portrait' },
  { name: 'Wallpaper', searchTerm: 'Wallpaper' },
];

/** shop page explanation text and search input, sits above subnav tabs */
const ShopPageHeader: React.FC<Props> = ({
  searchPageType = 'all',
  searchTerm = '',
  totalResults = 0,
}) => {
  const { t } = useTranslation();
  const [searchTermInInput, setSearchTermInInput] = useState(searchTerm);
  const [pathname, setPathname] = useState('/shop');
  const logBiEvent = useBiLogger();
  const isShopBannerTopicsFeatureEnabled = useFlag('shop_banner_topics');
  const title = searchTerm
    ? t('shopPage.header.titleWithTermAndCount', {
        searchTerm,
        formattedCount: shortenNumber(totalResults),
        count: totalResults,
      })
    : t('shopPage.header.title');

  // use the current window.location.pathname to allow the search tag href's
  // to remain in the same searchPageType (instead of sending them to 'all')
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const windowPathname = window.location.pathname;
      const newPathname = windowPathname.endsWith('/search')
        ? windowPathname
        : `${windowPathname}/search`;
      setPathname(newPathname);
    }
  }, []);

  const searchTags = useMemo(() => {
    return SEARCH_TAGS.map(tag => ({
      tagname: tag.name,
      href: `${pathname}?q=${encodeURIComponent(tag.searchTerm)}`,
      biData: BiData<SearchClickBiEvent>({
        evid: 710,
        search_terms: tag.searchTerm,
        sectionname: 'shop_tag',
      }),
      className: s['search-tag'],
      isSelected: tag.searchTerm === searchTerm,
    }));
  }, [pathname, searchTerm]);

  const submitCallback = useCallback(
    ev => {
      ev.preventDefault();
      const newSearchTerm = searchTermInInput.trim();

      logBiEvent(
        BiData<SearchClickBiEvent>({
          evid: 710,
          search_terms: newSearchTerm,
          sectionname: 'shop',
        })
      );

      window.location.assign(
        newSearchTerm
          ? getPathnameWithSearch(newSearchTerm)
          : getPathnameWithoutSearch()
      );
    },
    [searchTermInInput, logBiEvent]
  );

  return (
    <Section className={classnames(s['root'], searchTerm && s['with-search'])}>
      <Heading className={s['title']}>{title}</Heading>
      {!searchTerm && (
        <p className={s['subtitle']}>{t('shopPage.header.subtitle')}</p>
      )}

      <ThemeOverride theme={ThemeOptions.LIGHT}>
        <form className={s['search-form']} onSubmit={submitCallback}>
          <InputField
            className={s['search-input']}
            prefix={
              <PlainButtonOrLink onClick={submitCallback}>
                <VisuallyHidden>
                  {t('shopPage.header.a11y.searchButton')}
                </VisuallyHidden>
                <SearchIcon size={IconSize.SMALLER} disableDefaultColors />
              </PlainButtonOrLink>
            }
            placeholder={t('shopPage.header.searchFieldPlaceholder')}
            onChange={ev => setSearchTermInInput(ev.target.value)}
            value={searchTermInInput}
            name="q"
          />

          {isShopBannerTopicsFeatureEnabled && (
            <TagList
              className={s['search-tags']}
              size="small"
              tags={searchTags}
            />
          )}
        </form>
      </ThemeOverride>
    </Section>
  );
};
ShopPageHeader.displayName = 'ShopPageHeader';

export default ShopPageHeader;

// these are to handle the special /shop/search1=term vs /shop
// others: /shop/commissions/search1=term vs. /shop/commissions
function getPathnameWithoutSearch(): string {
  if (typeof window === 'undefined') {
    return '/shop';
  }
  const { pathname: windowPathname } = window.location;
  return windowPathname === '/shop/search'
    ? '/shop'
    : windowPathname.replace('/search', '');
}

function getPathnameWithSearch(searchTerm: string): string {
  if (typeof window === 'undefined') {
    return '/shop/search';
  }
  const { pathname: windowPathname } = window.location;
  const newPathname = windowPathname.endsWith('/search')
    ? windowPathname
    : `${windowPathname}/search`;
  return `${newPathname}?q=${encodeURIComponent(searchTerm)}`;
}
